/**
 * Couleurs de la charte client : validation hexadécimale stricte et contrôle de contraste WCAG 2.x.
 * Fonctions pures, sans dépendance (copie fonctionnellement identique côté nexxus-business-api).
 */

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

export type Rgb = readonly [number, number, number];

/** `#RRGGBB` strict (pas de forme courte, pas de noms CSS, pas de rgb()). */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX6.test(value);
}

/** Majuscules ; retourne null si invalide. Accepte `#RGB` à la saisie et l'étend. */
export function normalizeHexColor(value: string): string | null {
  const v = value.trim();
  if (/^#[0-9A-Fa-f]{3}$/.test(v)) {
    const r = v.charAt(1);
    const g = v.charAt(2);
    const b = v.charAt(3);
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return HEX6.test(v) ? v.toUpperCase() : null;
}

export function hexToRgb(hex: string): Rgb {
  if (!isHexColor(hex)) throw new Error('hex_invalide');
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

export function rgbToHex(rgb: Rgb): string {
  return `#${rgb
    .map((c) =>
      Math.max(0, Math.min(255, Math.round(c)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`.toUpperCase();
}

/** Luminance relative WCAG 2.x. */
export function relativeLuminance(hex: string): number {
  const lin = hexToRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (lin[0] ?? 0) + 0.7152 * (lin[1] ?? 0) + 0.0722 * (lin[2] ?? 0);
}

/** Rapport de contraste WCAG (1 à 21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Mélange `weight` (0..1) de `towards` dans `base`. */
export function mixColors(base: string, towards: string, weight: number): string {
  const a = hexToRgb(base);
  const b = hexToRgb(towards);
  const w = Math.max(0, Math.min(1, weight));
  return rgbToHex([a[0] + (b[0] - a[0]) * w, a[1] + (b[1] - a[1]) * w, a[2] + (b[2] - a[2]) * w]);
}

/** Seuil WCAG AA du texte normal : le texte des documents est blanc sur les aplats de la charte. */
export const WCAG_AA_NORMAL_TEXT = 4.5;
export const BRAND_TEXT_ON_COLOR = '#FFFFFF';

export interface ContrastIssue {
  readonly field: 'primary_color' | 'secondary_color';
  readonly ratio: number;
  readonly required: number;
}

/**
 * Le texte blanc des en-têtes, bandeaux et pieds de page est posé sur la couleur principale ET sur
 * la couleur secondaire (dégradés, intitulés de section) : les deux doivent atteindre 4,5:1.
 */
export function brandContrastIssues(primary: string, secondary: string): ContrastIssue[] {
  const issues: ContrastIssue[] = [];
  const p = contrastRatio(primary, BRAND_TEXT_ON_COLOR);
  if (p < WCAG_AA_NORMAL_TEXT) {
    issues.push({ field: 'primary_color', ratio: p, required: WCAG_AA_NORMAL_TEXT });
  }
  const s = contrastRatio(secondary, BRAND_TEXT_ON_COLOR);
  if (s < WCAG_AA_NORMAL_TEXT) {
    issues.push({ field: 'secondary_color', ratio: s, required: WCAG_AA_NORMAL_TEXT });
  }
  return issues;
}
